import { Injectable } from '@angular/core';
import { NavController, ModalController } from '@ionic/angular';
import { collection, collectionData, docData, Firestore, query, where, doc, DocumentReference, docSnapshots, CollectionReference, orderBy, limit, getDoc } from '@angular/fire/firestore';
import { distinctUntilChanged, filter, map } from 'rxjs/operators';
import { ProfileStateDispatcher } from 'src/app/core/state/profile/profile.dispatcher';
import { BehaviorSubject, Observable, Subscription, firstValueFrom, shareReplay } from 'rxjs';
import * as _ from 'lodash';
import { GameStoreDispatcher } from 'src/app/core/state/game/game.dispatcher';
import { FreePlayLobbyDispatcher } from 'src/app/core/state/freePlayLobby/freePlayLobby.dispatcher';
import { RingConfig, SngConfig } from 'src/app/core/state/freePlayLobby/freePlayLobby.state';
import { ClubService } from 'src/app/core/firebase/club.service';
import { CreateMttPayload, CreateSngPayload, CreateTablePayload, CurrencyType, DEFAULT_BOUNTY_SETTINGS, DEFAULT_MTT_TOURNAMENT_RULES, DEFAULT_MTT_TOURNAMENT_SETTINGS, DEFAULT_TABLE_RULES, DEFAULT_TABLE_SETTINGS, DEFAULT_TOURNAMENT_RULES, DEFAULT_TOURNAMENT_SETTINGS, IGameQueueItem, IGameQueueItemStatus, IMttFreePlayConfig, IMultiTableTournamentRules, IMultiTableTournamentState, ISitNGoTournamentRules, ISitNGoTournamentState, ITableState, ITournamentState, PrizeTypes, SeasonLeaderboardItem, TableType, TournamentStatus } from '@soposopo/sopo-models';
import { ApiService } from 'src/app/core/backend/api.service';
import { SopoLoadingService } from 'src/app/shared/services/sopo-loading/sopo-loading.service';
import { AuthStoreDispatcher } from '../state/auth/auth.dispatcher';
import { ComponentToLoad, GameToLoad } from '../state/game/game.state';
import { NavigationExtras } from '@angular/router';
import { SopoTransitionService } from 'src/app/shared/services/sopo-transition/sopo-transition.service';
import { DEFAULT_DISMISS_AFTER_TIME, SopoPopupService } from 'src/app/sopo-popup/service/sopo-popup.service';
import { SopoConfirmSettings } from 'src/app/sopo-popup/interfaces/confirm';
import { SopoDialogRef, SopoDialogState } from 'src/app/sopo-popup/interfaces/dialog';
import { DEFAULT_NECESSARY_POINTS, DEFAULT_RING_POINT_RESET_FREQUENCY, RingPointResetFrequency } from 'src/app/shared/utils/ring-points.utils';
import { DailyRingPointsComponent } from 'src/app/game/component/daily-ring-points/daily-ring-points.component';
import { IActiveGame } from 'src/app/sweepstakes/components/freeplay-sweepstakes-games/freeplay-sweepstakes-games.component';

enum ILeaderBoardCurrency {
    SWEEPS = 'global-sweeps',
    GOLD = 'global-gold',
}

const hri = require('human-readable-ids').hri;
@Injectable({
    providedIn: 'root'
})
export class FreePlayLobbyService {
    private activeSngTournamentsSubject = new BehaviorSubject<IActiveGame[]>([]);
    public activeSngTournaments$: Observable<IActiveGame[]> = this.activeSngTournamentsSubject.asObservable();

    private dailyRingPointsPopupRef: SopoDialogRef;
    tournamentSubscription: Subscription;

    constructor(
        public navCtrl: NavController,
        public modalController: ModalController,
        public loadingCtrl: SopoLoadingService,
        private profileDispatcher: ProfileStateDispatcher,
        private gameDispatcher: GameStoreDispatcher,
        private freePlayLobbyDispatcher: FreePlayLobbyDispatcher,
        private authStoreDispatcher: AuthStoreDispatcher,
        private clubService: ClubService,
        private apiService: ApiService,
        private profileService: ProfileStateDispatcher,
        private firestore: Firestore,
        private sopoTransitionService: SopoTransitionService,
        private sopoPopupService: SopoPopupService,
    ) {}

    public initializeClub(clubId: string) {
        this.clubService.watchClub(clubId);
    }

    public async initializeSubscriptions() {
        this.freePlayLobbyDispatcher.subscribeToConfigs();
    }

    async getTable(config: RingConfig, excludedGameIds?: string[]) {
        this.loadingCtrl.present('general');
        const excludeList = excludedGameIds ? excludedGameIds : await this.getExcludeList(config.type);

        const myId = await this.profileDispatcher.getMyID();
        const result  = (await this.apiService.getFreePlayGame({
            gameConfigId: config.gameConfigId,
            type: config.type,
            excludeList,
            playerId: myId})).result;
        const tableId = result ? result.tableId : null;

        this.loadingCtrl.dismiss('general');
        if(!tableId) {
            const message = `No table Id for gameConfigId: ${config.gameConfigId}, game type: ${config.type}`;
            console.error(message);
            throw new Error(message);
        }
        return tableId;
    }

    private async getTournamnt(config: SngConfig, excludedTournamentIds?: string[]) {
        this.loadingCtrl.present('general');
        const excludeList = excludedTournamentIds ? excludedTournamentIds : await this.getExcludeList(config.type);
        const myId = await this.profileDispatcher.getMyID();
        const response = (await this.apiService.getFreePlayGame({
            gameConfigId: config.gameConfigId,
            type: TableType.SNG,
            excludeList,
            playerId: myId})).result;
        const tournamentId = response.tournamentId;
        const playerAlreadySeated = response.playerAlreadySeated;

        this.loadingCtrl.dismiss('general');
        if(!tournamentId) {
            const message = `No tournament Id for gameConfigId: ${config.gameConfigId}, tournamentType: ${config.type}`;
            console.error(message);
            throw new Error(message);
        }
        return {tournamentId, playerAlreadySeated};
    }

    async openFreePlayRing(
        config: RingConfig,
        excludedGameIds: string[],
    ) {
        const gameLimitChecker = await this.gameLimitChecker();
        if (!gameLimitChecker) return;

        const tableId = await this.getTable(config, excludedGameIds);

        this.loadingCtrl.present('general');
        const game: GameToLoad = { componentType: 'game', gameStateId: tableId, tableId, tournamentId: '', clubId: "SOPO", type: TableType.RING, configId: config.gameConfigId };
        this.gameDispatcher.addNewGame(game, true);
    }

    setActiveSngTournaments(tournaments: IActiveGame[]): void {
        this.activeSngTournamentsSubject.next(tournaments);
    }

    getActiveSngTournaments(): Observable<IActiveGame[]> {
        return this.activeSngTournaments$;
    }

    public async openFreePlaySng(
        config: SngConfig,
        jackpotBuyIn: boolean,
        excludedTournamentIds: string[],
        conversionRate?: number,
    ) {
        const gameLimitChecker = await this.gameLimitChecker();
        if (!gameLimitChecker) return;

        const {tournamentId, playerAlreadySeated} = await this.getTournamnt(config, excludedTournamentIds);

        sessionStorage.setItem('tournamentType', TableType.SNG);
        sessionStorage.setItem('tournamentId', tournamentId);
        this.enterTournament(tournamentId, playerAlreadySeated, config.buyIn, config.currency, config.gameConfigId, jackpotBuyIn, conversionRate);
    }

    public async openRunningJackpotGame(tournament: ISitNGoTournamentState): Promise<void> {
        const gameLimitChecker = await this.gameLimitChecker(tournament, TableType.SNG);
        if (!gameLimitChecker) return;

        const clientID = await this.profileDispatcher.getMyID();
        const seatNr = tournament!.rules.seats;
        const occupiedSeatNr = tournament!.playerIDs.length + tournament!.eliminatedPlayers.length;
        const overlayType = tournament!.currency;

        if ((seatNr - occupiedSeatNr) > 0) {
            await this.navigateToTable(tournament.id, TableType.SNG);
            return;
        }

        this.enterTable(tournament.id, true, clientID, tournament.gameConfigId);
    }

    public async openRunningRingGame(game: ITableState) {
        const gameLimitChecker = await this.gameLimitChecker(game, TableType.RING);
        if (!gameLimitChecker) return;

        this.loadingCtrl.present('general');
        const gameToOpen: GameToLoad = { componentType: 'game', gameStateId: game.id, tableId: game.id, tournamentId: '', clubId: "SOPO", type: TableType.RING, configId: game.gameConfigId };
        this.gameDispatcher.addNewGame(gameToOpen, true);
    }

    private async checkCurrency(amount: number, currency: CurrencyType, jackpotBuyIn: boolean, conversionRate?: number) {
        const currencyToBeUsed = jackpotBuyIn ? 'jackpotTicket' : currency;
        const ownedInCurrency = await firstValueFrom(this.profileService.getProfileState()
        .pipe(
            distinctUntilChanged(),
            map(profileState => {
                if(!profileState || !profileState.profileData) return 0;
                return profileState.profileData[currencyToBeUsed];
            }),
        ));
        if(jackpotBuyIn && conversionRate && conversionRate !== 0) {
            amount = amount * conversionRate;
        }
        if ( ownedInCurrency < amount) {
            this.loadingCtrl.dismiss('general');
            await this.showNotEnoughCurrency();
            throw Error('Not enoungh currency!');
        }

    }

    private async enterTournament(tournamentId: string, playerAlreadySeated: boolean, buyIn: number, currency: CurrencyType, configId: string, jackpotBuyIn: boolean, conversionRate?: number) {
        if (!playerAlreadySeated ) {
            await this.checkCurrency(buyIn, currency, jackpotBuyIn, conversionRate);
        }

        const clientID = await this.profileService.getMyID();

        let dialogOpened = false;
        if (this.tournamentSubscription) this.tournamentSubscription.unsubscribe();
        this.tournamentSubscription = docSnapshots<ITournamentState>(doc(this.firestore, `tournaments/${tournamentId}`) as DocumentReference<ITournamentState>)
        .pipe(
            filter(snapshot => snapshot.exists() && !snapshot.metadata.fromCache),
            map(snapshot => snapshot.data()),
            distinctUntilChanged((prev, curr) => {
                return prev.status === curr.status &&
                    prev.playerIDs.length === curr.playerIDs.length &&
                    prev.eliminatedPlayers.length === curr.eliminatedPlayers.length &&
                    prev.rules.seats === curr.rules.seats &&
                    prev.currency === curr.currency;
            }),
        )
        .subscribe(async (tournamentDoc) => {
            const status = tournamentDoc!.status;
            const isRegistered = tournamentDoc!.playerIDs.indexOf(clientID) > -1;
            const seatNr = tournamentDoc!.rules.seats;
            const occupiedSeatNr = tournamentDoc!.playerIDs.length + tournamentDoc!.eliminatedPlayers.length;
            const overlayType = tournamentDoc!.currency;

            if(!isRegistered && !dialogOpened) {
                dialogOpened = true;
                await this.apiService.requestToRegister({
                    userID: clientID,
                    stack: buyIn,
                    latitude: 0,
                    longitude: 0,
                    clubId: "SOPO",
                    userClub: "SOPO",
                    useJackpotTicket: jackpotBuyIn,
                }, TableType.SNG, tournamentId);

                return;
            }

            if(isRegistered) {
                if((seatNr - occupiedSeatNr) > 0) {
                    await this.navigateToTable(tournamentId, TableType.SNG);
                    return;
                }
                this.enterTable(tournamentId, true, clientID, configId);
                return;
            }
        });
    }

    public async enterTable(tournamentId: string, toNav: boolean, clientID: string, configId: string) {
        this.tournamentSubscription?.unsubscribe();

        if(!tournamentId) {
            const message = `[FreePlayLobbyService] - enterTable() - No tournament Id for gameConfigId: ${configId}`;
            console.error(message);
            this.loadingCtrl.dismiss('general');
            throw new Error(message);
        }

        const collReference = collection(this.firestore, `tournaments/${tournamentId}/tables`);
        const queryTables = query(collReference, where('seats', 'array-contains', clientID));
        const tableId = await firstValueFrom(collectionData(queryTables).pipe(
            filter(tables => tables.length > 0),
            map(tables => tables[0].id),
        ));

        if(!tableId) {
            const message = `[FreePlayLobbyService] - enterTable() - No tableId Id for gameConfigId: ${configId} tournamentId: ${tournamentId}`;
            console.error(message);
            this.loadingCtrl.dismiss('general');
            throw new Error(message);
        }

        this.gameDispatcher.addNewGame(
            { componentType: 'game', gameStateId: tableId, tableId: tableId, tournamentId: tournamentId, clubId: "SOPO", type: TableType.SNG, configId },
            toNav,
        );
        this.loadingCtrl.dismiss('general');
    }

    private async navigateToTable(tournamentId: string, tournamentType: string) {
        this.tournamentSubscription?.unsubscribe();
        sessionStorage.setItem('tournamentType', tournamentType);
        sessionStorage.setItem('tournamentId', tournamentId);
        if (tournamentType == 'SNG') {
            const navigationExtras: NavigationExtras = {
                state: {
                    type: tournamentType,
                },
                replaceUrl: false,
            };
            this.navCtrl.navigateForward(`tournament-registration/SOPO/${tournamentId}`, navigationExtras);
        }
        this.loadingCtrl.dismiss('general');
    }

    navigateToSelectedPlay(playType: 'freeplay' | 'sweepstakes', noTransition = false, gameType: TableType = TableType.SNG) {
        let navigationExtras: NavigationExtras = {
            queryParams: {
                tabs: playType,
                noTransition,
                gameTab: gameType,
            }
        }
        this.navCtrl.navigateForward('lobby/home/sweepstakes', navigationExtras);
    }

    public async requestQueueAndOpenFreePlayJackpotSng(
        config: SngConfig,
        jackpotBuyIn: boolean,
        conversionRate?: number,
    ) {
        const gameLimitChecker = await this.gameLimitChecker();
        if (!gameLimitChecker) return;

        await this.checkCurrency(config.buyIn, config.currency, jackpotBuyIn, conversionRate);

        this.loadingCtrl.present('general');
        try {
            const myId = await this.profileDispatcher.getMyID();
            await this.apiService.getFreePlayGameQueue({
                gameConfigId: config.gameConfigId,
                type: TableType.SNG,
                playerId: myId,
                useJackpotTicket: jackpotBuyIn});
            this.navigateToQueuePage(config.gameConfigId, config.currency, config.seats);
        } catch (error) {
            this.sopoPopupService.showAlert('app', {
                header: error.error?.header ? error.error.header : 'An Error Occured',
                message: error.error?.message ? error.error.message : `An unexpected error occured while requesting a game.`,
                dismissAfter: DEFAULT_DISMISS_AFTER_TIME,
            });
            console.error('[FreePlayService] - requestQueueAndOpenFreePlayJackpotSng - ', error);
        } finally {
            this.loadingCtrl.dismiss('general');
        }
    }

    public async openRequestedGameQueue(gameQueue: IGameQueueItem, seatsNr: number): Promise<void> {
        const gameLimitChecker = await this.gameLimitChecker();
        if (!gameLimitChecker) return;

        await this.navigateToQueuePage(gameQueue.gameConfigId, gameQueue.currency, seatsNr);
    }

    private async navigateToQueuePage(gameConfigId: string, overlayType: CurrencyType, seatsNr: number): Promise<void> {
        const navigationExtras: NavigationExtras = {
            queryParams: {
                overlayType,
                seats: seatsNr,
            },
            replaceUrl: true,
        };
        this.navCtrl.navigateForward(`tournament-queue/${gameConfigId}`, navigationExtras);
    }

    private getGameQueuesObs(gameConfigId: string, currency: CurrencyType): Observable<IGameQueueItem[]> {
        const activeGameQueuesRef = collection(this.firestore, `/gameQueues/${gameConfigId}/queue`) as CollectionReference<IGameQueueItem>;
        const activeGameQueuesCollectionQuery = query(activeGameQueuesRef, where("currency", '==', currency));

        return collectionData(activeGameQueuesCollectionQuery).pipe(
            filter(data => data !== null),
            shareReplay({ bufferSize: 1, refCount: true }),
        ) as Observable<IGameQueueItem[]>;
    }

    private getWaitingGameQueuesObs(gameConfigId: string, currency: CurrencyType): Observable<IGameQueueItem[]> {
        const activeGameQueuesRef = collection(this.firestore, `/gameQueues/${gameConfigId}/queue`) as CollectionReference<IGameQueueItem>;
        const activeGameQueuesCollectionQuery = query(activeGameQueuesRef,
            where("currency", '==', currency),
            where("status", "in", [IGameQueueItemStatus.WAITING, IGameQueueItemStatus.PROCESSING]),
        );

        return collectionData(activeGameQueuesCollectionQuery).pipe(
            filter(data => data !== null),
            shareReplay({ bufferSize: 1, refCount: true }),
        ) as Observable<IGameQueueItem[]>;
    }

    public getWaitingQueueItemsObs(gameConfigId: string, currency: CurrencyType): Observable<IGameQueueItem[]> {
        const waitingQueueItems$ = this.getWaitingGameQueuesObs(gameConfigId, currency).pipe(
            map(queues => queues.filter(queue => !queue.tournamentId)),
        ) as Observable<IGameQueueItem[]>;

        return waitingQueueItems$;
    }

    public getQueueItemByDocIdObs(docId: string, gameConfigId: string, currency: CurrencyType): Observable<IGameQueueItem> {
        const usersActiveQueueItem$ = this.getGameQueuesObs(gameConfigId, currency).pipe(
            map(queues => queues.find(queue => queue.docId === docId)),
        ) as Observable<IGameQueueItem>;

        return usersActiveQueueItem$;
    }

    public showNotEnoughCurrency() {
        this.sopoPopupService.showAlert('app', {
            header: 'Free Play',
            message: 'Insufficient balance to play Jackpot.',
            dismissAfter: DEFAULT_DISMISS_AFTER_TIME,
        });
    }

    async runChecks(isFreePlay = true): Promise<void> {
        const isOpenedClubGame = await firstValueFrom(this.gameDispatcher.getState().pipe(
            map(gameState => gameState.gamesList.find(game => game.componentType === 'game' && game.clubId !== 'SOPO') !== undefined)
        ));

        if (isOpenedClubGame) {
            this.showOpenedClubTableAlert(isFreePlay);
            throw Error('Club game still oepne!');
        }
    }

    private async getExcludeList(configType: string): Promise<string[]> {
        return await firstValueFrom(this.gameDispatcher.getState()
        .pipe(
            map(games => games.gamesList.filter(gamesList => gamesList.componentType === 'game' && gamesList.type === configType).map((game: GameToLoad) => configType === TableType.SNG ? game.tournamentId : game.tableId ))
        ))
    }

    private async showOpenedClubTableAlert(isFreePlay = true): Promise<void> {
        this.sopoPopupService.showAlert('app', {
            header: isFreePlay ? 'Free Play' : 'Quickplay',
            message: `Please close all club tables in order to play on ${isFreePlay ? 'free play' : 'quickplay'} table.`,
            dismissAfter: DEFAULT_DISMISS_AFTER_TIME,
        });
    }

    private async gameLimitChecker(game?: ISitNGoTournamentState | ITableState, gameType?: TableType ): Promise<boolean> {
        const gameList = await firstValueFrom(this.gameDispatcher.getState().pipe(map(game => game.gamesList)));

        if (game) {
            let searchedGame: ComponentToLoad;
            switch (gameType) {
                case TableType.SNG:
                    searchedGame = gameList.find(gameInList => gameInList.componentType === 'game' && gameInList.tournamentId === game.id);
                    break;
                case TableType.RING:
                    searchedGame = gameList.find(gameInList => gameInList.componentType === 'game' && gameInList.tableId === game.id);
                    break;
            }
            if (searchedGame) return true;
        }

        if (gameList.length >= 4) {
            const alert = this.sopoPopupService.showAlert('app', {
                message: `You can't open more than 4 tables.`,
                dismissAfter: 5000,
            });
            return false;
        }
        return true;
    }

    async createQuickplay( selectedGame: string) {
        const userID = await this.authStoreDispatcher.getMyID();
        this.loadingCtrl.present('general');
        if (selectedGame === 'Ring') {
            const tableName = hri.random();
            const payload: CreateTablePayload = {
                adminID: userID,
                tableID: '',
                clubID: 'SOPO',
                allTables: [],
                tableRules: DEFAULT_TABLE_RULES,
                tableSettings: {
                    ...DEFAULT_TABLE_SETTINGS,
                    name: tableName,
                },
                isUnion: false,
                isQuickplay: true,
                currency: CurrencyType.NONE
            }
            try {
                const tableCreated = await this.apiService.createTable(payload);
                const tableId = tableCreated.result.tableId;
                const game: GameToLoad = { componentType: 'game', gameStateId: tableId, tableId, tournamentId: '', clubId: "SOPO", type: TableType.RING };
                await this.gameDispatcher.addNewGame(game, true, undefined, undefined, true);
                this.loadingCtrl.dismiss('general');
            } catch (error) {
                this.loadingCtrl.dismiss('general');
                throw error;
            }
        }

        if (selectedGame === 'SNG') {
            const tournamentName = hri.random();
            const tournamentSettings = {
                ...DEFAULT_TOURNAMENT_SETTINGS,
                name: tournamentName,
                restricted: true,
                currentBlindValue: "15/30",
                nextBlindValue: "20/40",
            }
            const tournamentRules = {
                ...DEFAULT_TOURNAMENT_RULES,
                blindStructureType: "Standard",
                buyIn: 0,
                burn: 10,
                prizeType: PrizeTypes.WINNER_TO_TAKE_ALL,
                isFreeRoll: true,
            }
            const payload: CreateSngPayload = {
                adminID: userID,
                tournamentID: '',
                clubID: 'SOPO',
                allTables: [],
                sngRules: tournamentRules as ISitNGoTournamentRules,
                sngSettings: tournamentSettings,
                isUnion: false,
                tournamentType: 'SNG',
                currency: CurrencyType.NONE,
                isQuickplay: true,
                tournamentOwner: userID
            }
            try {
                const tournamentCreated = await this.apiService.createTournament(payload);
                const tournamentID = tournamentCreated.result.tournamentId;
                await this.waitingDocumentCreation(tournamentID);
                this.setSessionStorage('SNG', tournamentID);
                const navigationExtras: NavigationExtras = {
                    state: {
                        type: 'SNG',
                    },
                    replaceUrl: true,
                };
                await this.navCtrl.navigateRoot(`tournament-registration/SOPO/${tournamentID}`, navigationExtras);
            } catch (error) {
                this.loadingCtrl.dismiss('general');
                throw error;
            }
        }

        if (selectedGame === 'MTT') {
            const startingDate = Date.now() + 30 * 60 * 1000;
            const tournamentName = hri.random();
            const tournamentSettings = {
                ...DEFAULT_MTT_TOURNAMENT_SETTINGS,
                name: tournamentName,
                addonPeriodEnd: 0,
                blindChangedOn: 0,
                breakGoingToEnd: 0,
                authorizedToRegister: true,
                restricted: true,
                startingDate,
                breakPeriod: 30,
                breakLength: 2,
                lateRegGoingToEnd: 0,
                nextBlind: 0,
                nextBreakOn: 0,
                lateRegistrationPeriod: 5 * 60000,
                nextBlindValue: "15/30",
                unregistrationTimeLimit: 5,
            }
            const tournamentRules: IMultiTableTournamentRules = {
                ...DEFAULT_MTT_TOURNAMENT_RULES,
                addonPeriod: 0,
                blindStructureType: "Standard",
                blindsTime: 3,
                botsNrPerPlayer: 0,
                burn: 10,
                maxPlayersPerTable: 6,
                maxHumanPlayers: 30,
                minHumanPlayers: 0,
                buyIn: 0,
                seats: 6,
                isFreeRoll: true,
                prizeType: PrizeTypes.WINNER_TO_TAKE_ALL,
            }
            const payload: CreateMttPayload = {
                adminID: userID,
                tournamentID: '',
                clubID: 'SOPO',
                allTables: [],
                mttRules: tournamentRules,
                mttSettings: tournamentSettings,
                mttBountySettings: DEFAULT_BOUNTY_SETTINGS,
                isUnion: false,
                tournamentType: 'MTT',
                multiDayTournamentSettings: {
                    levelsArray: [],
                    nextStopIndex: null,
                    stoppedAt: null,
                },
                currency: CurrencyType.NONE,
                isQuickplay: true,
                tournamentOwner: userID,
            }
            try {
                const tournamentCreated = await this.apiService.createMtTournament(payload);
                const tournamentID = tournamentCreated.result.tournamentId;
                await this.waitingDocumentCreation(tournamentID);
                this.setSessionStorage('MTT', tournamentID);
                const navigationExtras: NavigationExtras = {
                    state: {
                        type: 'MTT',
                    },
                    replaceUrl: true,
                };
                await this.navCtrl.navigateRoot(`tournament-registration/SOPO/${tournamentID}`, navigationExtras);
            } catch (error) {
                this.loadingCtrl.dismiss('general');
                throw error;
            }
        }
    }

    public async navigateAwayFromJackpotQueue(){
        const myId = await this.authStoreDispatcher.getMyID();
        let confirmation = localStorage.getItem(`dontShowAgain_sweepsNotificationWarning_${myId}`) === 'true' ? true : false;
        if(!confirmation) {
            const options: SopoConfirmSettings = {
                data: {
                    header: `Important!`,
                    body: "Please ensure you turn on notifications for SOPO in your settings so you can be notified when the game begins.",
                    okText: `Go back`,
                    cancelText: `Cancel`,
                    dontShowAgain: "sweepsNotificationWarning",
                },
            };
            confirmation = await this.sopoPopupService.promptForConfirmation('app', options);
        }
        return confirmation;
    }

    async waitingDocumentCreation(tournamentId: string) {
        try {
            const docReference = doc(this.firestore, `tournaments/${tournamentId}`) as DocumentReference<ITournamentState>;
            await firstValueFrom(docData(docReference).pipe(filter(docData => docData !== undefined)));
        } catch(error) {
            throw error;
        }
    }

    setSessionStorage(tournamentType: string, tournamentId: string) {
        sessionStorage.setItem('tournamentType', tournamentType);
        sessionStorage.setItem('tournamentId', tournamentId);
    }

    public async getFreerollMttGameConfig(currency: CurrencyType): Promise<IMttFreePlayConfig> {
        const collectionRef = collection(this.firestore, 'config/MTT/gameConfigs') as CollectionReference<IMttFreePlayConfig>;
        const collectionQuery = query(collectionRef, where('currency', '==', currency), where('necessaryPoints', '>', 0));
        const config$ = collectionData<IMttFreePlayConfig>(collectionQuery).pipe(
            map(configs => configs.length > 0 ? configs[0] : null),
        );
        const config = await firstValueFrom(config$);
        return config;
    }

    public async getRingPointResetSettings(): Promise<{ frequency?: RingPointResetFrequency; resetTime?: string }> {
        const docRef = doc(this.firestore, 'config/tournament-settings');
        const snapshot = await getDoc(docRef);
        if (!snapshot.exists()) return {};
        const data = snapshot.data();
        return {
            frequency: data?.['ringPointResetFrequency'] as RingPointResetFrequency | undefined,
            resetTime: data?.['ringPointResetTime'] as string | undefined,
        };
    }

    public async getLatestTournament(currency: CurrencyType, gameConfigId: string): Promise<IMultiTableTournamentState> {
        const collectionRef = collection(this.firestore, '/tournaments') as CollectionReference<IMultiTableTournamentState>;
        const collectionQuery = query(collectionRef,
            orderBy("dateCreated", "desc"),
            where("clubID", "==", "SOPO"),
            where("tournamentType", "==", TableType.MTT),
            where("currency", '==', currency),
            where('gameConfigId', '==', gameConfigId),
            limit(1),
        );
        const tournaments$ = collectionData<IMultiTableTournamentState>(collectionQuery).pipe(map(tournaments => {
            return tournaments = tournaments.filter(tournament => tournament.status !== TournamentStatus.TOURNAMENT_ENDED);
        }));
        const tournaments = await firstValueFrom(tournaments$);
        return tournaments.length > 0 ? tournaments[0] : undefined;
    }

    public async getDailyRingPointsLeaderboardList(currency: CurrencyType, tournamentId: string): Promise<SeasonLeaderboardItem[]> {
        if (!tournamentId) return [];

        let leaderBoardCurrency: ILeaderBoardCurrency;
        switch(currency) {
            case CurrencyType.SWEEPBUCK:
                leaderBoardCurrency = ILeaderBoardCurrency.SWEEPS;
                break;
            default:
                leaderBoardCurrency = ILeaderBoardCurrency.GOLD;
                break;
        }

        const leaderboardId = `leaderboard:seasons:${leaderBoardCurrency}:qualify-points:${tournamentId}`;
        const leaderboardList = await this.apiService.getSeasonLeaderboard({ leaderboardId });
        return leaderboardList;
    }

    public async getCurrencyAllTimeLeaderboardList(currency: CurrencyType, gameType?: TableType): Promise<SeasonLeaderboardItem[]> {
        let leaderBoardCurrency: ILeaderBoardCurrency;
        switch(currency) {
            case CurrencyType.SWEEPBUCK:
                leaderBoardCurrency = ILeaderBoardCurrency.SWEEPS;
                break;
            default:
                leaderBoardCurrency = ILeaderBoardCurrency.GOLD;
                break;
        }

        let leaderboardId = `leaderboard:seasons:${leaderBoardCurrency}:alltime`;
        switch (gameType) {
            case TableType.RING:
                leaderboardId = `leaderboard:seasons:${leaderBoardCurrency}:ring:alltime`;
                break;
            case TableType.MTT:
            case TableType.SNG:
                return []; //not implemented yet
        }

        const leaderboardList = await this.apiService.getSeasonLeaderboard({ leaderboardId });
        return leaderboardList;
    }

    async showDailyRingPointsPopup(gameStateId: string, currency: CurrencyType, requestedByUser?: boolean): Promise<void> {
        const userId = await this.profileDispatcher.getMyID();
        const config = await this.getFreerollMttGameConfig(currency);
        if (!config || !config?.gameConfigId) return;

        const tournament = await this.getLatestTournament(currency, config.gameConfigId);
        if (!tournament) return;

        const leaderboardList = await this.getDailyRingPointsLeaderboardList(currency, tournament.id);
        const userOnBoard = leaderboardList.find(lb => lb.userId === userId);

        const necessaryPoints = config.necessaryPoints || DEFAULT_NECESSARY_POINTS;
        let currentPoints = 0;
        if (userOnBoard)  currentPoints = userOnBoard.points;

        if (requestedByUser || currentPoints < necessaryPoints) {
            if (this.dailyRingPointsPopupRef === undefined || (this.dailyRingPointsPopupRef && this.dailyRingPointsPopupRef.getState() === SopoDialogState.CLOSED)) {
                let frequency: RingPointResetFrequency = DEFAULT_RING_POINT_RESET_FREQUENCY;
                try {
                    const settings = await this.getRingPointResetSettings();
                    if (settings.frequency) frequency = settings.frequency;
                } catch (error) {
                    console.error('[FreePlayLobbyService] - getRingPointResetSettings failed, using default - ', error);
                }

                this.dailyRingPointsPopupRef = this.sopoPopupService.openDialog('game-container', DailyRingPointsComponent, {
                    data: {
                        necessaryPoints,
                        currentPoints,
                        gameStateId,
                        currency,
                        frequency,
                    },
                    closeButton: true,
                }, gameStateId);
            }
        }
    }

    ngOnDestroy(): void {
        // Called once, before the instance is destroyed.
        // Add 'implements OnDestroy' to the class.
        this.destroySubscriptions();
        this.modalController.getTop().then((v) => {
            if (v){
                this.modalController.dismiss()
            }
        });
    }

    destroySubscriptions() {
        if (this.tournamentSubscription) this.tournamentSubscription.unsubscribe();
        this.freePlayLobbyDispatcher.destroySubscriptionsAndResetState();
    }
}
